import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Upload, Link as LinkIcon, Loader2 } from 'lucide-react'
import type { FileRecord } from '../../types'

export type FileSourceType = 'upload' | 'external'

export interface FileMetadataFormValues {
  source: FileSourceType
  file?: File | null
  externalUrl?: string
  filename: string
  description: string
  privacy: 'public' | 'trip_members' | 'event_members' | 'private' | 'custom'
  tags: string[]
  eventId?: string | null
  fileType?: string
  fileSize?: number
}

interface FileMetadataModalProps {
  open: boolean
  mode: 'create' | 'edit'
  onClose: () => void
  onSubmit: (values: FileMetadataFormValues) => Promise<void>
  initialData?: Partial<FileRecord> | null
  events: Array<{ id: string; title: string }>
  contextLabel?: string
}

const PRIVACY_OPTIONS: Array<{
  value: FileMetadataFormValues['privacy']
  label: string
  description: string
}> = [
  { value: 'public', label: 'Public', description: 'Accessible without authentication when storage allows public access.' },
  { value: 'trip_members', label: 'Trip Members', description: 'Visible to users participating in the trip.' },
  { value: 'event_members', label: 'Event Members', description: 'Restricted to members of the linked event.' },
  { value: 'private', label: 'Private', description: 'Only the uploader can view this file.' },
  { value: 'custom', label: 'Custom', description: 'Access controlled via explicit permissions.' },
]

const DEFAULT_PRIVACY: FileMetadataFormValues['privacy'] = 'private'

function parseTags(tags?: any): string[] {
  if (!tags) return []
  if (Array.isArray(tags)) return tags.filter(Boolean).map(String)
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags)
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String)
    } catch {
      return tags
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean)
    }
  }
  return []
}

export function FileMetadataModal({
  open,
  mode,
  onClose,
  onSubmit,
  initialData = null,
  events,
  contextLabel,
}: FileMetadataModalProps) {
  const [source, setSource] = useState<FileSourceType>(() => initialData?.externalUrl ? 'external' : 'upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filename, setFilename] = useState(initialData?.filename || initialData?.originalFilename || '')
  const [externalUrl, setExternalUrl] = useState(initialData?.externalUrl || '')
  const [description, setDescription] = useState(initialData?.description || '')
  const [privacy, setPrivacy] = useState<FileMetadataFormValues['privacy']>(initialData?.privacy || DEFAULT_PRIVACY)
  const [tags, setTags] = useState<string[]>(parseTags(initialData?.tags))
  const [eventId, setEventId] = useState(initialData?.eventId || '')
  const [fileType, setFileType] = useState(initialData?.fileType || '')
  const [fileSize, setFileSize] = useState<number | ''>(initialData?.fileSize || '')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setSelectedFile(null)
      setError(null)
      setSubmitting(false)
      return
    }

    setSource(initialData?.externalUrl ? 'external' : 'upload')
    setFilename(initialData?.filename || initialData?.originalFilename || '')
    setExternalUrl(initialData?.externalUrl || '')
    setDescription(initialData?.description || '')
    setPrivacy(initialData?.privacy || DEFAULT_PRIVACY)
    setTags(parseTags(initialData?.tags))
    setEventId(initialData?.eventId || '')
    setFileType(initialData?.fileType || '')
    setFileSize(initialData?.fileSize || '')
    setSelectedFile(null)
    setError(null)
    setSubmitting(false)
  }, [open, initialData])

  const eventOptions = useMemo(() => events.map(event => ({ value: event.id, label: event.title })), [events])

  if (!open) {
    return null
  }

  const handleTagAdd = (tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed || tags.includes(trimmed)) return
    setTags(prev => [...prev, trimmed])
  }

  const handleTagRemove = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag))
  }

  const handleFileChange = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    const file = fileList[0]
    setSelectedFile(file)
    setFilename(file.name)
    setFileType(file.type)
    setFileSize(file.size)
    setSource('upload')
  }

  const validate = () => {
    if (source === 'upload' && mode === 'create' && !selectedFile) {
      setError('Select a file to upload.')
      return false
    }
    if (source === 'external' && !externalUrl.trim()) {
      setError('Provide a valid external URL.')
      return false
    }
    if (!filename.trim()) {
      setError('Filename is required.')
      return false
    }
    return true
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!validate()) return

    setSubmitting(true)
    try {
      await onSubmit({
        source,
        file: selectedFile,
        externalUrl: externalUrl.trim() || undefined,
        filename: filename.trim(),
        description: description.trim(),
        privacy,
        tags,
        eventId: eventId ? eventId : undefined,
        fileType: fileType || undefined,
        fileSize: typeof fileSize === 'number' ? fileSize : undefined,
      })
    } catch (err: any) {
      console.error('FileMetadataModal submit error:', err)
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save file. Please try again.'
      setError(message)
      setSubmitting(false)
      return
    }
    setSubmitting(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'create' ? 'Add File' : 'Edit File'}
            </h2>
            {contextLabel && (
              <p className="text-xs text-gray-500 mt-0.5">{contextLabel}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5 max-h-[calc(80vh-120px)] overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Source
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSource('upload')}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                    source === 'upload'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Upload className="h-4 w-4" />
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setSource('external')}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                    source === 'external'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <LinkIcon className="h-4 w-4" />
                  External URL
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Privacy
              </label>
              <select
                value={privacy}
                onChange={(e) => setPrivacy(e.target.value as FileMetadataFormValues['privacy'])}
                className="input w-full"
              >
                {PRIVACY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                {PRIVACY_OPTIONS.find(option => option.value === privacy)?.description}
              </p>
            </div>
          </div>

          {source === 'upload' ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                File
              </label>
              <input
                ref={fileInputRef}
                type="file"
                className="input w-full disabled:opacity-60 disabled:cursor-not-allowed"
                onChange={(event) => handleFileChange(event.target.files)}
                disabled={mode === 'edit'}
              />
              {selectedFile && (
                <div className="text-xs text-gray-500">
                  {selectedFile.name} • {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • {selectedFile.type || 'unknown type'}
                </div>
              )}
              {mode === 'edit' && initialData?.url && (
                <div className="text-xs text-gray-500">
                  File content replacements are not supported in this editor. Delete and upload a new file if you need to refresh the binary.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                External URL
              </label>
              <input
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                className="input w-full"
                placeholder="https://cdn.example.com/path/file.pdf"
              />
              <p className="text-xs text-gray-500">
                The file will remain hosted externally. Ensure CORS is configured if the file should be accessible from the app.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Display Name
            </label>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              className="input w-full"
              placeholder="tickets.pdf"
              required
            />
          </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        File Type (MIME)
      </label>
      <input
        type="text"
        value={fileType}
        onChange={(e) => setFileType(e.target.value)}
        className="input w-full"
        placeholder="application/pdf"
      />
    </div>
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        File Size (bytes)
      </label>
      <input
        type="number"
        min={0}
        value={fileSize === '' ? '' : fileSize}
        onChange={(e) => setFileSize(e.target.value ? Number(e.target.value) : '')}
        className="input w-full"
        placeholder="Optional"
      />
    </div>
  </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input w-full min-h-[100px]"
              placeholder="What is this file about?"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 px-2 py-1 text-xs"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag)}
                    className="text-blue-700 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              className="input w-full"
              placeholder="Add tag and press Enter"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const value = (e.target as HTMLInputElement).value.trim()
                  if (value) {
                    handleTagAdd(value)
                    ;(e.target as HTMLInputElement).value = ''
                  }
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Attach to Event (optional)
            </label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="input w-full"
            >
              <option value="">No event — trip level file</option>
              {eventOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {initialData?.url && source === 'upload' && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              Current storage key: <code className="font-mono">{initialData.filePath || '—'}</code>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-60"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Save File' : 'Update File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FileMetadataModal
